import { PF } from './tagFont';
import { priceTag, pricingOn, getPricing, purityPctOf, inr } from '@/lib/tagPricing';
import { rateCardById } from '@/lib/rateCards';
import { tagMaterials } from '@/components/jewellery/JewelleryTagViewer';

const g3 = (n) => Number(n || 0).toFixed(3);
const c2 = (n) => Number(n || 0).toFixed(2);
const natural = (a, b) => String(a || '').localeCompare(String(b || ''), undefined, { numeric: true, sensitivity: 'base' });

// 'show' = priced list, 'dash' = rate-card user without prices (values as "-"), 'none' = no rate access.
export const summaryValues = () => (pricingOn() ? 'show' : getPricing().canView ? 'dash' : 'none');
export const purityLabel = (d) => (d.purityText && d.purityText !== '—' ? d.purityText : 'No purity');
export const purityTitle = (label) => (label === 'No purity' ? 'No purity' : `${label} Gold`);
export const catOf = (m) => (m.materialType === 'diamond' ? 'dia' : m.materialType === 'color_stone' ? 'stone' : m.materialType === 'pearl' ? 'pearl' : 'other');

// Purity labels, highest purity first.
export function purityOrder(datas) {
  const m = new Map();
  datas.forEach((d) => { const l = purityLabel(d); if (!m.has(l)) m.set(l, purityPctOf(d) || 0); });
  return [...m.entries()].sort((a, b) => b[1] - a[1] || natural(a[0], b[0])).map(([l]) => l);
}

const add = (a, b) => (a == null || b == null ? null : a + b);
const blank = () => ({ count: 0, gross: 0, net: 0, gold: 0, labour: 0, charges: 0, dia: { ct: 0, pcs: 0, amt: 0 }, stone: { ct: 0, pcs: 0, amt: 0 }, pearl: { ct: 0, pcs: 0, amt: 0 }, other: { ct: 0, pcs: 0, amt: 0 } });

function addPiece(s, d, materials, p) {
  s.count += 1;
  s.gross += Number(d.gross || 0);
  s.net += Number(Number(d.net || 0).toFixed(3));
  materials.forEach((m) => {
    const c = s[catOf(m)];
    c.ct += Number(m.carats || 0);
    c.pcs += m.hidePieces ? 0 : Number(m.pieces || 0);
    if (p) c.amt = add(c.amt, p.byKey[m.key]?.amount ?? null);
  });
  if (p) {
    s.gold = add(s.gold, p.goldAmt);
    s.labour = add(s.labour, p.labourAmt);
    p.charges.forEach((c) => { s.charges = add(s.charges, c.amount); });
  }
}

const totalOf = (s) => [s.gold, s.dia.amt, s.stone.amt, s.pearl.amt, s.other.amt, s.labour, s.charges].reduce(add, 0);

// Purity-wise summaries + grand total of a list (amounts exactly as on the tags, so they tally).
export function buildSummary(datas, rmMode = 'expanded', materialsOf = (d) => tagMaterials(d, rmMode)) {
  const values = summaryValues();
  const card = values === 'show' ? rateCardById(getPricing().cardId) : null;
  const by = new Map(purityOrder(datas).map((l) => [l, blank()]));
  const grand = blank();
  datas.forEach((d) => {
    const materials = materialsOf(d);
    const p = card ? priceTag(d, rmMode, card, materials) : null;
    addPiece(by.get(purityLabel(d)), d, materials, p);
    addPiece(grand, d, materials, p);
  });
  const fin = (s) => ({ ...s, total: totalOf(s) });
  return { values, groups: [...by.entries()].map(([label, s]) => ({ label, title: purityTitle(label), ...fin(s) })), grand: { label: 'Grand total', title: 'Grand total', ...fin(grand) } };
}

export function summaryRows(s, values) {
  const mat = (k, label) => [label, `${c2(s[k].ct)} ct${s[k].pcs ? ` · ${s[k].pcs} pcs` : ''}`, s[k].amt];
  const rows = [
    ['Gross weight', `${g3(s.gross)} g`, undefined],
    ['Net weight (gold value)', `${g3(s.net)} g`, s.gold],
    mat('dia', 'Diamonds'), mat('stone', 'Colour stones'), mat('pearl', 'Pearls'),
    ...(s.other.ct || s.other.pcs ? [mat('other', 'Other materials')] : []),
  ];
  if (values !== 'none') rows.push(['Labour', '', s.labour], ['Other charges', '', s.charges], ['Total value', '', s.total, true]);
  return rows;
}
export const valueText = (v, values) => (v === undefined ? '' : values !== 'show' || v == null ? '-' : `Rs. ${inr(v)}`);

const NAVY = [30, 42, 74]; const GREEN = [15, 107, 79]; const AMBER = [254, 243, 199]; const LINE = [217, 222, 231];

function drawBox(pdf, x, y, w, s, values, dark) {
  const rows = summaryRows(s, values);
  const RH = 6.2; const HB = 8;
  const h = HB + rows.length * RH + 2;
  pdf.setDrawColor(...(dark ? GREEN : NAVY)); pdf.setLineWidth(0.35);
  pdf.roundedRect(x, y, w, h, 2, 2, 'S');
  pdf.setFillColor(...(dark ? GREEN : NAVY)); pdf.rect(x, y, w, HB, 'F');
  pdf.setFont(PF(), 'bold'); pdf.setFontSize(10); pdf.setTextColor(255, 255, 255);
  pdf.text(`${s.title} · ${s.count} piece${s.count === 1 ? '' : 's'}`, x + 4, y + 5.4);
  if (values !== 'none') { pdf.setFont(PF(), 'normal'); pdf.setFontSize(7.5); pdf.text('VALUE', x + w - 4, y + 5.4, { align: 'right' }); }
  let ry = y + HB;
  rows.forEach(([label, qty, val, bold]) => {
    if (bold) { pdf.setFillColor(...AMBER); pdf.rect(x + 0.3, ry + 0.4, w - 0.6, RH, 'F'); }
    const ty = ry + RH / 2 + 1.5;
    pdf.setFont(PF(), bold ? 'bold' : 'normal'); pdf.setFontSize(9); pdf.setTextColor(...NAVY);
    pdf.text(label, x + 4, ty);
    pdf.setFont(PF(), 'bold'); pdf.setFontSize(9);
    if (qty) pdf.text(qty, x + w * 0.62, ty, { align: 'right' });
    if (values !== 'none') pdf.text(valueText(val, values), x + w - 4, ty, { align: 'right' });
    ry += RH;
    if (!bold) { pdf.setDrawColor(...LINE); pdf.setLineWidth(0.15); pdf.line(x + 3, ry, x + w - 3, ry); }
  });
  return h;
}

// Summary page(s) appended to a jsPDF list: one box per purity, then the grand total box.
export function addSummaryPdf(pdf, datas, rmMode, title) {
  if (!datas.length) return;
  const sum = buildSummary(datas, rmMode);
  const W = 210; const H = 297; const MX = 14; const bw = W - 2 * MX;
  const head = () => {
    pdf.addPage('a4', 'portrait');
    pdf.setFont(PF(), 'bold'); pdf.setFontSize(14); pdf.setTextColor(...NAVY);
    pdf.text('Summary', MX, 16);
    pdf.setFont(PF(), 'normal'); pdf.setFontSize(8.5); pdf.setTextColor(100, 116, 139);
    pdf.text(pdf.splitTextToSize(`${title || 'Selected pieces'} · ${datas.length} piece${datas.length === 1 ? '' : 's'}${sum.groups.length > 1 ? ` · ${sum.groups.length} gold purities` : ''}`, bw)[0], MX, 21.5);
    return 27;
  };
  let y = head();
  const boxes = [...sum.groups.map((g) => [g, false]), ...(sum.groups.length > 1 ? [[sum.grand, true]] : [])];
  boxes.forEach(([s, dark]) => {
    const need = 8 + summaryRows(s, sum.values).length * 6.2 + 2;
    if (y + need > H - 14) y = head();
    y += drawBox(pdf, MX, y, bw, dark ? s : { ...s, title: sum.groups.length > 1 ? s.title : `${s.title} · list total` }, sum.values, dark) + 6;
  });
}

// ── Batch-print Summary box: one row per purity + Grand total ('weights' | 'full') ──
const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

export function summaryTable(datas, rmMode, kind) {
  const sum = buildSummary(datas, rmMode);
  const priced = sum.values === 'show';
  const rs = (v) => (v == null ? '-' : `Rs. ${inr(v)}`);
  const mat = (k) => (s) => ({ main: `${c2(s[k].ct)} ct`, sub: [s[k].pcs ? `${s[k].pcs} pcs` : '', priced ? rs(s[k].amt) : ''].filter(Boolean).join(' · ') });
  const cols = [
    { h: 'Gold purity', left: true, get: (s) => ({ main: s.title }) },
    { h: 'Pieces', get: (s) => ({ main: String(s.count) }) },
    { h: 'Gross wt', get: (s) => ({ main: `${g3(s.gross)} g` }) },
    { h: 'Net wt', get: (s) => ({ main: `${g3(s.net)} g`, sub: priced ? `Gold ${rs(s.gold)}` : '' }) },
    ...(kind === 'full' ? [{ h: 'Diamonds', get: mat('dia') }, { h: 'Colour stones', get: mat('stone') }, { h: 'Pearls', get: mat('pearl') }] : []),
    ...(kind === 'full' && priced ? [{ h: 'Labour', get: (s) => ({ main: rs(s.labour) }) }, { h: 'Other charges', get: (s) => ({ main: rs(s.charges) }) }] : []),
    ...(priced ? [{ h: 'Total value', strong: true, get: (s) => ({ main: rs(s.total) }) }] : []),
  ];
  const rows = [...sum.groups, { ...sum.grand, title: 'Grand total', grand: true }];
  return { cols, rows, priced };
}

export function summaryTableHtml(datas, rmMode, kind, title) {
  const { cols, rows } = summaryTable(datas, rmMode, kind);
  // Many columns: smaller type and wrapping so the table never runs past the page edge.
  const k = cols.length > 6;
  const pad = k ? '6px 5px' : '8px 9px';
  const nw = k ? 'normal' : 'nowrap';
  const th = cols.map((c) => `<th style="padding:${k ? '6px 5px' : '7px 9px'};text-align:${c.left ? 'left' : 'right'};font:600 ${k ? 8.5 : 10}px sans-serif;letter-spacing:.06em;text-transform:uppercase;color:#64748b;border-bottom:1px solid #d9dee7">${esc(c.h)}</th>`).join('');
  const tr = rows.map((r, i) => `<tr style="background:${r.grand ? '#e8f3ee' : i % 2 ? '#f8fafc' : '#fff'}">${cols.map((c) => {
    const v = c.get(r);
    return `<td style="padding:${pad};text-align:${c.left ? 'left' : 'right'};border-top:${r.grand ? '2px solid #0f6b4f' : '1px solid #eef1f5'};vertical-align:top">
      <div style="font:${r.grand || c.strong ? 700 : 600} ${k ? (c.left ? 10.5 : 10) : (c.left ? 12 : 11.5)}px ${c.left ? 'sans-serif' : 'ui-monospace,Menlo,monospace'};color:${r.grand ? '#0f6b4f' : '#1e2a4a'};white-space:${nw}">${esc(v.main)}</div>
      ${v.sub ? `<div style="margin-top:2px;font:500 ${k ? 8.5 : 9.5}px sans-serif;color:#0f6b4f;white-space:${nw}">${esc(v.sub)}</div>` : ''}</td>`;
  }).join('')}</tr>`).join('');
  return `<div style="break-inside:avoid;page-break-inside:avoid;padding:6mm 0;font-family:sans-serif" data-testid="batch-summary-box">
    <div style="border:1.5px solid #1e2a4a;border-radius:12px;overflow:hidden">
      <div style="background:#1e2a4a;color:#fff;padding:10px 14px;display:flex;justify-content:space-between;align-items:baseline">
        <span style="font:700 16px sans-serif;letter-spacing:.02em">Summary</span>
        <span style="font:400 11px sans-serif;opacity:.85">${esc(title || 'Tags')} · ${datas.length} piece${datas.length === 1 ? '' : 's'}</span>
      </div>
      <table style="width:100%;border-collapse:collapse;table-layout:${k ? 'fixed' : 'auto'}">${`<thead><tr>${th}</tr></thead>`}<tbody>${tr}</tbody></table>
    </div></div>`;
}

// Height (mm) of the PDF summary box, used to decide whether it fits under the last tags.
export function summaryHeightMm(datas, rmMode, kind) {
  if (!datas.length || kind === 'none') return 0;
  const n = summaryTable(datas, rmMode, kind).rows.length;
  return 11 + 10 + n * (n > 8 ? 9 : 11) + 2;
}

// startY: page position under the last tags; the box goes there when it fits, else on a new page.
export function addSummaryTablePdf(pdf, datas, rmMode, kind, title, startY) {
  if (!datas.length || kind === 'none') return;
  const { cols, rows } = summaryTable(datas, rmMode, kind);
  const here = startY != null && startY + summaryHeightMm(datas, rmMode, kind) <= pdf.internal.pageSize.getHeight() - 10;
  let land = cols.length > 6;
  if (here) land = pdf.internal.pageSize.getWidth() > pdf.internal.pageSize.getHeight();
  else pdf.addPage('a4', land ? 'landscape' : 'portrait');
  const tight = !land && cols.length > 6;
  const W = land ? 297 : 210; const H = land ? 210 : 297; const MX = 12;
  const bw = W - 2 * MX; const first = tight ? 24 : 30;
  // Weighted widths: narrow Pieces, wider raw material / value columns.
  const wt = cols.map((c, i) => (i === 0 ? 0 : c.h === 'Pieces' ? 0.55 : ['Diamonds', 'Colour stones', 'Pearls', 'Total value'].includes(c.h) ? 1.35 : 1));
  const unit = (bw - first) / wt.reduce((a, b) => a + b, 0);
  const ws = wt.map((w) => w * unit);
  const xs = cols.map((c, i) => (i === 0 ? MX + 4 : MX + first + ws.slice(1, i + 1).reduce((a, b) => a + b, 0) - 3));
  let y = here ? startY : 14;
  // Shrink text until it fits its column instead of cutting it.
  const fit = (t, w, size) => { let f = size; pdf.setFontSize(f); while (f > 4.6 && pdf.getTextWidth(t) > w) { f -= 0.3; pdf.setFontSize(f); } return t; };
  const HB = 11; const HR = tight ? 10 : 8; const RH = rows.length > 8 ? 9 : 11;
  const boxH = HB + HR + rows.length * RH + 2;
  pdf.setDrawColor(...NAVY); pdf.setLineWidth(0.5);
  pdf.roundedRect(MX, y, bw, Math.min(boxH, H - y - 10), 3, 3, 'S');
  pdf.setFillColor(...NAVY); pdf.roundedRect(MX, y, bw, HB, 3, 3, 'F'); pdf.rect(MX, y + HB - 3, bw, 3, 'F');
  pdf.setFont(PF(), 'bold'); pdf.setFontSize(13); pdf.setTextColor(255, 255, 255);
  pdf.text('Summary', MX + 5, y + 7.4);
  pdf.setFont(PF(), 'normal'); pdf.setFontSize(8.5);
  pdf.text(pdf.splitTextToSize(`${title || 'Selected pieces'} · ${datas.length} piece${datas.length === 1 ? '' : 's'}`, bw / 2)[0], MX + bw - 5, y + 7.4, { align: 'right' });
  y += HB;
  pdf.setFillColor(243, 245, 249); pdf.rect(MX + 0.25, y, bw - 0.5, HR, 'F');
  pdf.setFont(PF(), 'bold'); pdf.setFontSize(7); pdf.setTextColor(100, 116, 139);
  cols.forEach((c, i) => {
    const w = (i === 0 ? first - 5 : ws[i] - 2);
    const ln = pdf.splitTextToSize(c.h.toUpperCase(), w).slice(0, 2);
    ln.forEach((t, li) => pdf.text(fit(t, w, 7), xs[i], y + (ln.length > 1 ? 4 + li * 3.2 : HR / 2 + 1.2), { align: i === 0 ? 'left' : 'right' }));
    pdf.setFontSize(7);
  });
  y += HR;
  rows.forEach((r, ri) => {
    if (y + RH > H - 10) { pdf.addPage('a4', land ? 'landscape' : 'portrait'); y = 14; }
    if (r.grand) { pdf.setFillColor(232, 243, 238); pdf.rect(MX + 0.25, y, bw - 0.5, RH, 'F'); pdf.setDrawColor(...GREEN); pdf.setLineWidth(0.5); pdf.line(MX, y, MX + bw, y); } else if (ri % 2) { pdf.setFillColor(248, 250, 252); pdf.rect(MX + 0.25, y, bw - 0.5, RH, 'F'); }
    cols.forEach((c, i) => {
      const v = c.get(r);
      const left = i === 0;
      pdf.setFont(PF(), 'bold');
      pdf.setTextColor(...(r.grand ? GREEN : NAVY));
      pdf.text(fit(v.main, left ? first - 5 : ws[i] - 2, tight ? (left ? 8 : 7.5) : (left ? 9.5 : 9)), xs[i], y + (v.sub ? 4.6 : RH / 2 + 1.4), { align: left ? 'left' : 'right' });
      if (v.sub) { pdf.setFont(PF(), 'normal'); pdf.setTextColor(...GREEN); pdf.text(fit(v.sub, ws[i] - 2, tight ? 5.6 : 6.3), xs[i], y + 8.4, { align: 'right' }); }
    });
    y += RH;
  });
}
